// react
import React from "react";
import { Link } from "yarrr";
import PropTypes from "prop-types";

// components
import Spinner from "Components/Spinner";

// styles
import css from "./Button.styl";

const sizes = {
	default: 36,
	small: 24,
	medium: 30,
};

/**
 * A versatile button component that supports different colors, sizes, and states.
 * @component
 * @example
 * // Basic usage
 * <Button onClick={() => console.log('clicked')}>Click me</Button>
 *
 * // With color
 * <Button color="blue">Blue Button</Button>
 *
 * // As a link
 * <Button href="/some-path">Link Button</Button>
 */
export default class Button extends React.PureComponent {
	static propTypes = {
		/** Content to be rendered inside the button */
		children: PropTypes.node,
		/** Click handler function */
		onClick: PropTypes.func,
		/** Whether the button is in a loading state */
		isRequesting: PropTypes.bool,
		/** Whether the button should open in a new tab (when used as a link) */
		external: PropTypes.bool,
		/** Whether the button is disabled */
		disabled: PropTypes.bool,
		/** Button type */
		type: PropTypes.string,
		/** URL for link button */
		href: PropTypes.string,
		/**
		 * Button color variant
		 * @type {('blue'|'red'|'green'|'orange'|'black'|'grey'|'gray')}
		 */
		color: PropTypes.string,
		/** Additional CSS class name */
		className: PropTypes.string,
		/** Additional inline styles */
		style: PropTypes.object,
		/** Size of the loading spinner */
		spinnerSize: PropTypes.number,
		/**
		 * Button size variant
		 * @type {('default'|'small'|'medium')}
		 */
		size: PropTypes.oneOf(Object.keys(sizes)),
	};

	static defaultProps = {
		children: "",
		className: "",
		style: {},
		isRequesting: false,
		disabled: false,
		color: "blue",
		onClick: () => {},
		type: "default",
		href: null,
		external: false,
		spinnerSize: 26,
	};

	handleClick = () => {
		if (!this.props.isRequesting && !this.props.disabled) {
			this.props.onClick();
		}
	};

	render = () => {
		const {
			type,
			children,
			className,
			color,
			isRequesting,
			spinnerSize,
			href,
			external,
			size = "default",
		} = this.props;

		const style = {
			height: sizes[size],
			lineHeight: `${sizes[size]}px`,
			...this.props.style,
		};

		const content = isRequesting ? (
			<Spinner className={css.spinner} style={{ margin: "0 auto" }} size={spinnerSize} color="white" />
		) : (
			children
		);

		if (href) {
			let otherProps = {};

			if (external) {
				otherProps = {
					target: "_blank",
					rel: "noopener noreferrer",
				};
				return (
					<a
						href={href}
						className={`${css.Button} ${type} ${className} ${color}`}
						onClick={this.handleClick}
						style={{ ...style }}
						{...otherProps}
					>
						{content}
					</a>
				);
			} else {
				return (
					<Link
						href={href}
						className={`${css.Button} ${type} ${className} ${color}`}
						onClick={this.handleClick}
						style={{ ...style }}
						{...otherProps}
					>
						{content}
					</Link>
				);
			}
		}

		return (
			<button
				className={`${css.Button} ${type} ${className} ${color}`}
				disabled={this.props.disabled}
				onClick={this.handleClick}
				style={{ ...style }}
			>
				{content}
			</button>
		);
	};
}
